import { Inject, Injectable } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';

import * as uuid from 'uuid';

import { MessageService } from 'primeng/api';
import {
    BehaviorSubject,
    EMPTY,
    Observable,
    catchError,
    combineLatest,
    distinctUntilChanged,
    filter,
    forkJoin,
    map,
    mergeMap,
    of,
    shareReplay,
    startWith,
    Subject,
    switchMap,
    take,
    tap,
    throwError
} from 'rxjs';

import { RelativeTimezoneDateTypeEnum } from '../../api/mentions/enum/relative-timezone-date-type.enum';
import { DateQueryModel } from '../../Common/Forms/models/date-query.model';
import { CurationAgentsApiService } from '../../api/agents/curation-agents.api.service';
import {
    AgentAdhocSourceResponse,
    CurationActionTypeEnum,
    CuratorQuotaResponse,
    CurationAgentSourceStatusEnum
} from '../../api/agents/models/curation-agent.api';
import { folderActionTypes, tagActionTypes } from '../enums/curation-agent-action-type.enum';
import { CurationAgentActionModel } from '../models/curation-agent-action.model';
import { RSSFeedService } from '../../Monitor/Shared/services/rss-feed.service';
import { AgentSourceIndexService } from './agent-source-index.service';
import { AgentUsageService, SourceTotals } from './agent-usage.service';
import { ConfirmDialogComponent } from '../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { ConfirmDialogModel } from '../../Common/Dialogs/models/confirm-dialog.model';
import { CoreDialogService } from '../../Core/Dialogs/services/dialog.service';
import { CoverageAnalysisService } from '../../Monitor/Shared/services/coverage-analysis.service';
import { CurrentUserStateService } from '../../Core/Users/services/current-user-state.service';
import { curationPromptType } from '../enums/curation-prompt-type.const';
import { LocalizationService } from '../../Core/Localization/services/localization.service';
import { messageSeverityEnum } from '../../Common/Misc/enums/message-severity.enum';
import { SavedSearchType } from '../../Common/MonitorShared/enum/saved-search-type.enum';
import { FolderService } from '../../Monitor/Core/services/folder.service';
import { TopicsService } from '../../Monitor/Core/services/topics.service';
import { TagsService } from '../../Common/Tags/services/tags.service';
import { TagGroupsService } from '../../Common/Tags/services/tag-groups.service';
import { SourceGroupService } from '../../Common/Sources/services/source-group.service';
import { MediaSubscriptionService } from '../../Monitor/Core/services/media-subscription.service';
import { AgentService } from '../../Social/Intelligence/services/agent.service';
import { ThemeService } from '../../Social/Intelligence/services/theme.service';
import { SavedMentionSearchesApi } from '../../api/mentions/saved-mention-searches.api';
import { SavedMentionSearchApiModel } from '../../api/mentions/model/saved-mention-search.api.model';
import { SearchCriteriaModel } from '../../api/mentions/model/search-criteria.model';
import { BasicNumberFilterModel } from '../../api/mentions/model/basic-number-filter.model';
import { MentionSearchCriteriaModel } from '../../Monitor/Shared/models/mention-search-criteria.model';
import { MonitoringBackEndDataSourceModel } from '../../Monitor/Shared/models/monitoring-back-end-data-source.model';
import { MentionSearchQueryModel } from '../../Monitor/Shared/models/mention-search-query.model';
import { MonitoringSeriesTypeEnum } from '../../Monitor/Shared/enums/monitoring-series-type.enum';
import { CurationAgentModel } from '../models/curation-agent.model';
import { sourceTypeLabels } from '../enums/source-icons.const';
import { CurationAgentSourceModel } from '../models/curation-agent-source.model';
import { AgentScopeModel } from '../models/agent-scope.util';
import { AgentRouteSectionEnum, agentSectionRoute } from '../AutomationAgentsRouter/automation-agents-route.const';

/** What `/saved-mention-searches` accepts for a name - anything longer is a 400 */
const maxSavedSearchNameLength: number = 64;

/** Key an action target's name is cached under - ids are only unique within their own kind */
export function actionNameKey(type: CurationActionTypeEnum, id: number | string): string {
    return `${type}:${id}`;
}

/** Key a source's display name is cached under, since ids are only unique within a source type */
export function sourceNameKey(type: string, id: number | string): string {
    return `${type}:${id}`;
}

@Injectable()
export class CurationAgentsService {

    private readonly refreshSubject: BehaviorSubject<void> = new BehaviorSubject<void>(null);
    private readonly refreshQuotaSubject: BehaviorSubject<void> = new BehaviorSubject<void>(null);

    /**
     * The account's quota pool. Shared so the management page, the usage panel and its expanded view
     * all read one fetch rather than one each.
     */
    readonly quota$: Observable<CuratorQuotaResponse> = this.refreshQuotaSubject.asObservable().pipe(
        mergeMap(() => this.curationAgentsApi.getQuota()),
        shareReplay(1)
    );

    /**
     * A source was paused or reactivated. The status itself is on the shared agent, so the views
     * follow it on their own - this is for what they derive from it, like row menu labels.
     */
    private readonly sourceStatusChangedSubject: Subject<void> = new Subject<void>();
    readonly sourceStatusChanged$: Observable<void> = this.sourceStatusChangedSubject.asObservable();

    /** The agent whose sections are open, set from the agent route */
    private readonly currentAgentIdSubject: BehaviorSubject<number> = new BehaviorSubject<number>(null);

    /**
     * That agent, loaded in full. Shared on purpose: the sidebar and the dashboard both list its
     * sources, and rendering one instance is what keeps them in step - pausing a source in one is
     * the same object the other is bound to. Re-reads after any create/update/delete.
     */
    readonly currentAgent$: Observable<CurationAgentModel> = combineLatest([
        this.currentAgentIdSubject.pipe(distinctUntilChanged()),
        this.refreshSubject
    ]).pipe(
        // A failed load must not kill the stream, or switching agents stops working
        switchMap(([agentId]) => agentId ? this.get(agentId).pipe(catchError(() => of(null))) : of(null)),
        filter(agent => !!agent),
        shareReplay(1)
    );

    /**
     * Source ids the agent's left hand panel has ticked, sent to advanced monitoring as includes.
     * Inclusions rather than exclusions, so a source removed from the agent drops out of scope even
     * though its id stays on the mentions it saved. Advanced monitoring ORs these with the ad hoc
     * includes - a mention carries a source id or an ad hoc id, never both.
     *
     * Null while nothing is narrowed - before the panel has published for the open agent, or with
     * everything ticked - which the sections read as the agent filter alone.
     */
    private readonly selectedSourcesSubject: BehaviorSubject<number[]> = new BehaviorSubject<number[]>(null);
    readonly selectedSources$: Observable<number[]> = this.selectedSourcesSubject.asObservable();

    /** Ad hoc runs the left hand panel has ticked - see {@link selectedSources$} */
    private readonly selectedAdHocEventsSubject: BehaviorSubject<number[]> = new BehaviorSubject<number[]>(null);
    readonly selectedAdHocEvents$: Observable<number[]> = this.selectedAdHocEventsSubject.asObservable();

    /**
     * What the left hand panel's ticks - Other among them - narrow the sections' searches by. Null
     * while nothing is narrowed. The ticked lists above are what the panel shows; this is what the
     * searches send, which with Other ticked is exclusions rather than those lists - see
     * AgentScopeModel.
     */
    private readonly agentScopeSubject: BehaviorSubject<AgentScopeModel> = new BehaviorSubject<AgentScopeModel>(null);
    readonly agentScope$: Observable<AgentScopeModel> = this.agentScopeSubject.asObservable();

    /**
     * The window the agent's sections report over. Shared so moving between the dashboard and the
     * mention feed keeps whatever range was picked, rather than each starting over.
     */
    private readonly selectedDateRangeSubject: BehaviorSubject<DateQueryModel> =
        new BehaviorSubject<DateQueryModel>(DateQueryModel.getRelativeTimezoneQuery(
            RelativeTimezoneDateTypeEnum.LAST_24_HOURS
        ));
    readonly selectedDateRange$: Observable<DateQueryModel> = this.selectedDateRangeSubject.asObservable();

    /**
     * Whether there is any allowance left to run on, which puts every agent on the account into
     * standby when there is not. `remaining` covers both ways that happens: a pool that has been
     * spent, and an account with no pool configured at all - which reads as zero here, since the
     * endpoint 404s for it.
     */
    readonly quotaExceeded$: Observable<boolean> = this.quota$.pipe(
        map(quota => quota.remaining <= 0),
        shareReplay(1)
    );

    /**
     * Every curation agent on the account. Re-emits after any create/update/delete, and is shared so
     * the management page and the agent sidebar read one fetch rather than one each.
     *
     * Each source is stamped with what it has drawn from the quota over the last day, which the
     * list response does not carry but the cards report on.
     */
    readonly agents$: Observable<CurationAgentModel[]> = this.refreshSubject.asObservable().pipe(
        mergeMap(() => forkJoin([
            this.curationAgentsApi.list(),
            this.agentUsageService.totalsBySource(DateQueryModel.getLast24HoursQuery())
        ])),
        map(([agents, totals]) => agents.map(agent => {
            const model = new CurationAgentModel(agent);

            model.sources.forEach(source => {
                source.analyzedLast24h = totals.get(source.id)?.analyzed ?? 0;
                source.curatedLast24h = totals.get(source.id)?.curated ?? 0;
            });

            return model;
        })),
        shareReplay(1)
    );

    /** Whether the account has any agents yet - an account with none is offered its first one */
    readonly hasAgents$: Observable<boolean> = this.agents$.pipe(
        map(agents => !!agents?.length),
        distinctUntilChanged()
    );

    /**
     * Display names for everything an agent can pull from, keyed by {@link sourceNameKey}.
     *
     * Shared and replayed on purpose: TopicsService.all$ is cold, so every subscription is another
     * GET. Resolving once here keeps that to a single request no matter how many cards are open, and
     * lets the card read a plain string rather than an observable built during change detection.
     */
    readonly sourceNames$: Observable<Map<string, string>> = combineLatest([
        this.namesOf(MonitoringSeriesTypeEnum.topic, this.topicsService.getTopics()),
        this.namesOf(MonitoringSeriesTypeEnum.folder, this.folderService.all$),
        this.namesOf(MonitoringSeriesTypeEnum.tag, this.tagsService.all$),
        this.namesOf(MonitoringSeriesTypeEnum.tagGroup, this.tagGroupsService.getTagGroupsList()),
        this.namesOf(MonitoringSeriesTypeEnum.sourceGroup, this.sourceGroupService.all$),
        this.namesOf(MonitoringSeriesTypeEnum.subscription, this.mediaSubscriptionService.all$),
        this.namesOf(MonitoringSeriesTypeEnum.socialTopic, this.agentService.all$),
        this.namesOf(MonitoringSeriesTypeEnum.theme, this.themeService.all$),
        this.namesOf(MonitoringSeriesTypeEnum.aiAgent, this.agents$)
    ]).pipe(
        map(entries => new Map<string, string>(entries.flat())),
        shareReplay(1)
    );

    /**
     * Names of the curation prompts on the account, keyed by id. Shared and replayed for the same
     * reason as {@link sourceNames$}: the cards read a plain string during change detection.
     */
    readonly promptNames$: Observable<Map<number, string>> = this.coverageAnalysisService.customInsights$.pipe(
        map(prompts => new Map<number, string>(
            (prompts ?? [])
                .filter(prompt => prompt.promptType === curationPromptType)
                .map(prompt => [prompt.id, prompt.name])
        )),
        shareReplay(1)
    );

    /** The viewer's own timezone, so every date the agent screens show is in the one they set */
    readonly userTimeZone$: Observable<string> = this.currentUserStateService.getPreferences().pipe(
        filter(preferences => !!preferences),
        map(preferences => preferences.timeZone),
        shareReplay(1)
    );

    constructor(
        private curationAgentsApi: CurationAgentsApiService,
        private currentUserStateService: CurrentUserStateService,
        private agentUsageService: AgentUsageService,
        private messageService: MessageService,
        private coreDialogService: CoreDialogService,
        private localizationService: LocalizationService,
        private coverageAnalysisService: CoverageAnalysisService,
        private topicsService: TopicsService,
        private folderService: FolderService,
        private tagsService: TagsService,
        private tagGroupsService: TagGroupsService,
        private sourceGroupService: SourceGroupService,
        private mediaSubscriptionService: MediaSubscriptionService,
        private agentService: AgentService,
        private themeService: ThemeService,
        private savedSearches: SavedMentionSearchesApi,
        private rssFeedService: RSSFeedService,
        private agentSourceIndexService: AgentSourceIndexService,
        @Inject(APP_BASE_HREF) private appBaseHref: string
    ) {}

    /**
     * One type's {@link sourceNames$} entries, keyed by {@link sourceNameKey}.
     *
     * Each list starts as an empty array so the combined map is built as soon as the page opens,
     * rather than waiting for the slowest list to arrive, and is refilled as each one loads. A list
     * that fails is treated as empty too: an account without social or subscriptions, for instance,
     * should still see its topics and folders named.
     */
    private namesOf(
        type: MonitoringSeriesTypeEnum,
        source: Observable<{ id?: number; name: string }[]>
    ): Observable<[string, string][]> {
        return source.pipe(
            startWith([]),
            catchError(() => of([])),
            map(items => (items ?? []).map(item => [sourceNameKey(type, item.id), item.name] as [string, string]))
        );
    }

    /** One agent's ad hoc curation runs, newest first. Re-reads after a new submission */
    /** Every ad hoc run on the account, newest first. Re-reads after a new submission */
    accountAdHocRuns(): Observable<AgentAdhocSourceResponse[]> {
        return this.refreshSubject.asObservable().pipe(
            mergeMap(() => this.curationAgentsApi.listAccountAdhocRuns().pipe(catchError(() => of([])))),
            shareReplay(1)
        );
    }

    adHocRuns(agentId: number): Observable<AgentAdhocSourceResponse[]> {
        return this.refreshSubject.asObservable().pipe(
            mergeMap(() => this.curationAgentsApi.listAdhocRuns(agentId).pipe(catchError(() => of([])))),
            shareReplay(1)
        );
    }

    /** What one agent has analyzed and curated from one of its sources over a window */
    sourceTotals(agentId: number, sourceId: number, dateRange: DateQueryModel): Observable<SourceTotals> {
        return this.agentUsageService.totalsBySource(AgentUsageService.reportingRange(dateRange), agentId).pipe(
            map(totals => totals.get(sourceId) ?? { analyzed: 0, curated: 0 })
        );
    }

    setCurrentAgent(agentId: number): void {
        // The last agent's ticks mean nothing on this one - left in place they would scope the new
        // agent's sections to ids it does not have until the panel catches up
        if (agentId !== this.currentAgentIdSubject.value) {
            this.selectedSourcesSubject.next(null);
            this.selectedAdHocEventsSubject.next(null);
            this.agentScopeSubject.next(null);
        }

        this.currentAgentIdSubject.next(agentId);
    }

    setSelectedAdHocEvents(eventIds: number[]): void {
        this.selectedAdHocEventsSubject.next(eventIds);
    }

    setSelectedSources(sourceIds: number[]): void {
        this.selectedSourcesSubject.next(sourceIds);
    }

    setAgentScope(scope: AgentScopeModel): void {
        this.agentScopeSubject.next(scope);
    }

    setSelectedDateRange(dateRange: DateQueryModel): void {
        this.selectedDateRangeSubject.next(dateRange);
    }

    refresh(): void {
        this.refreshSubject.next();
        this.refreshQuotaSubject.next();
        // Agents and their sources have changed, so whoever marks those sources has to re-read them
        this.agentSourceIndexService.refresh();
    }

    /** A single agent, carrying its sources and both action lists inline */
    get(id: number): Observable<CurationAgentModel> {
        return this.curationAgentsApi.get(id).pipe(
            map(agent => new CurationAgentModel(agent)),
            switchMap(model => this.loadSourceFilters(model)),
            switchMap(model => this.loadActionNames(model)),
            // The list stamps these on, and expanding a card replaces its sources with these ones -
            // so they have to carry the counts too, or the card's figures drop back to zero
            switchMap(model => this.agentUsageService
                .totalsBySource(DateQueryModel.getLast24HoursQuery(), model.id)
                .pipe(map(totals => {
                    model.sources.forEach(source => {
                        source.analyzedLast24h = totals.get(source.id)?.analyzed ?? 0;
                        source.curatedLast24h = totals.get(source.id)?.curated ?? 0;
                    });

                    return model;
                })))
        );
    }

    /**
     * Fills each source's picker back in with the filters it was saved with. They live in a saved
     * mention search rather than on the source row, which is also where the worker reads them from.
     */
    private loadSourceFilters(agent: CurationAgentModel): Observable<CurationAgentModel> {
        const filtered = agent.sources.filter(source => !!source.filterSearchCriteriaId);

        if (!filtered.length) {
            return of(agent);
        }

        return forkJoin(filtered.map(source => this.savedSearches.getById(source.filterSearchCriteriaId).pipe(
            tap(saved => source.filterLoaded(
                new MentionSearchCriteriaModel(saved as unknown as SearchCriteriaModel)
            )),
            // A filter that has gone missing must not stop the agent from opening
            catchError(() => of(null))
        ))).pipe(map(() => agent));
    }

    /**
     * The filters live in saved mention searches of their own, so they go when whatever pointed at
     * them does - a removed source, a replaced one, or a deleted agent. Left behind they would sit
     * in advanced monitoring forever, hidden and unreachable.
     *
     * Failures are swallowed: the source is already gone, and a stranded filter must not be
     * reported as a failed removal.
     */
    private deleteSourceFilters(sources: { filterSearchCriteriaId?: number }[]): Observable<unknown> {
        const ids = sources
            .map(source => source.filterSearchCriteriaId)
            .filter(filterSearchCriteriaId => !!filterSearchCriteriaId);

        if (!ids.length) {
            return of(null);
        }

        return forkJoin(ids.map(id => this.savedSearches.deleteById(id).pipe(catchError(() => of(null)))));
    }

    /**
     * Writes any newly picked filters into saved mention searches, so the source rows have
     * something to point at. Hidden, since they belong to the agent rather than the user's list.
     *
     * A row that already has one is updated in place: creating a second would collide on the name,
     * and keeping the id means the source row itself does not have to be replaced - so it holds on
     * to its status and scan history.
     */
    private saveSourceFilters(agentId: number, agent: CurationAgentModel): Observable<unknown> {
        const changed = agent.completeSources.filter(source => source.filterChanged);

        if (!changed.length) {
            return of(null);
        }

        return forkJoin(changed.map(source => this.saveSourceFilter(agentId, agent, source)));
    }

    private saveSourceFilter(
        agentId: number,
        agent: CurationAgentModel,
        source: CurationAgentSourceModel
    ): Observable<unknown> {
        const existingId = source.filterSearchCriteriaId;

        // Filters cleared: the saved search has nothing left to hold, so it goes with them
        if (!source.hasFilters) {
            source.filterPersisted(null);

            return existingId ? this.deleteSourceFilters([{ filterSearchCriteriaId: existingId }]) : of(null);
        }

        const search: SavedMentionSearchApiModel = {
            name: this.sourceFilterName(agentId, agent, source),
            // Its own type keeps it out of the topic lists, which hidden alone does not
            type: SavedSearchType.AGENT,
            hidden: true,
            query: source.filterQuery
        };

        if (existingId) {
            return this.savedSearches.update({ ...search, id: existingId }).pipe(
                tap(() => source.filterPersisted(existingId))
            );
        }

        return this.savedSearches.create(search).pipe(tap((savedSearchId: number) => source.filterPersisted(savedSearchId)));
    }

    /**
     * Writes what narrows one ad hoc run - the picker's filters and the range chosen for it - into
     * a hidden saved search, so the run can be submitted as a source reference rather than a bare
     * query and the service records the source it actually ran over. Resolves to the search's id,
     * or null where the run needs no narrowing beyond the source itself.
     */
    saveAdHocFilter(source: CurationAgentSourceModel, dateRange: DateQueryModel): Observable<number> {
        const filters = source?.dataSource?.filterSearchCriteria?.query?.clone() ?? new MentionSearchQueryModel();

        // Unlike a live source, which runs continuously, a run is scoped to the range it was given,
        // and a reference is resolved over the whole source - so the dates travel in the filter
        filters.dateQuery = dateRange;

        const search: SavedMentionSearchApiModel = {
            name: this.adHocFilterName(source),
            type: SavedSearchType.AGENT,
            hidden: true,
            query: filters.getApiEntity()
        };

        return this.savedSearches.create(search);
    }

    private adHocFilterName(source: CurationAgentSourceModel): string {
        return `Ad hoc ${this.filterNameTail(source)}`;
    }

    /**
     * Saved search names are unique across the account, and an agent can hold the same source more
     * than once with different filters - so the source alone does not identify the search. The tail
     * carries a stamp to tell those apart, and the agent's name is trimmed to leave room for it.
     */
    private sourceFilterName(agentId: number, agent: CurationAgentModel, source: CurationAgentSourceModel): string {
        const tail = ` (${agentId}) ${this.filterNameTail(source)}`;

        return `${agent.name ?? ''}`.slice(0, Math.max(0, maxSavedSearchNameLength - tail.length)).trim() + tail;
    }

    /** The part that makes the name unique: what it points at, and when it was written */
    private filterNameTail(source: CurationAgentSourceModel): string {
        const stamp = `${Date.now().toString(36)}${uuid.v4().slice(0, 4)}`;

        return `(${source?.dataSource?.type}:${source?.dataSource?.id}:${stamp})`;
    }

    /**
     * Creates or updates depending on whether the agent already has an id, then reconciles its
     * sources. Actions ride along on the create/update body; sources have their own endpoints.
     * Resolves to the agent id.
     */
    save(agent: CurationAgentModel): Observable<number> {
        const saveAgent = agent.id
            ? this.curationAgentsApi.update(agent.id, agent.toUpdateRequest())
            : this.curationAgentsApi.create(agent.toCreateRequest());

        return saveAgent.pipe(
            switchMap(saved => this.saveSources(saved.id, agent).pipe(map(() => saved.id))),
            tap(() => {
                this.refresh();
                this.growl(
                    messageSeverityEnum.success,
                    'AutomationAgents.growl.saved.summary',
                    'AutomationAgents.growl.saved.detail',
                    agent.name
                );
            }),
            catchError(error => {
                this.growl(
                    messageSeverityEnum.error,
                    'AutomationAgents.growl.saveFailed.summary',
                    'AutomationAgents.growl.saveFailed.detail',
                    agent.name
                );

                return throwError(() => error);
            })
        );
    }

    /**
     * Flips a single source between active and paused without touching the rest of the agent. The
     * switch is moved by whoever called this, so a failure has to put it back - nothing else knows
     * what it was.
     *
     * Called from a source's own panel, the growl names the agent rather than the source -
     * "Agent Activated" - since it is the agent that was switched there.
     */
    setSourceStatus(
        agent: CurationAgentModel,
        source: CurationAgentSourceModel,
        previousStatus?: CurationAgentSourceStatusEnum,
        fromSource: boolean = false
    ): Observable<unknown> {
        if (!agent.id || !source.id) {
            return of(null);
        }

        const status = source.isActive
            ? CurationAgentSourceStatusEnum.active
            : CurationAgentSourceStatusEnum.paused;

        return this.curationAgentsApi.updateSourceStatus(agent.id, source.id, { status: status }).pipe(
            // The service has the last word on the status: activating a source on an account with no
            // quota left parks it in standby rather than making it active
            tap(saved => {
                source.status = saved?.status ?? status;
                this.sourceStatusChangedSubject.next();
                // Pausing or resuming a source changes whether it counts as live
                this.agentSourceIndexService.refresh();
            }),
            // Both the title and the message name the new status, so the growl waits on the name
            tap(saved => fromSource
                ? this.growlWithSourceName(
                    source,
                    messageSeverityEnum.success,
                    this.agentStatusKey('summary', saved?.status ?? status),
                    this.agentStatusKey('detail', saved?.status ?? status),
                    agent.name,
                    this.localizationService.transform(sourceTypeLabels[source.dataSource?.type])
                )
                : this.growlWithSourceName(
                    source,
                    messageSeverityEnum.success,
                    this.sourceStatusSummary(saved?.status ?? status),
                    this.sourceStatusDetail(saved?.status ?? status)
                )),
            catchError(() => {
                // Put back where it was - a standby source that failed to pause stays on standby
                source.status = previousStatus ?? (status === CurationAgentSourceStatusEnum.active
                    ? CurationAgentSourceStatusEnum.paused
                    : CurationAgentSourceStatusEnum.active);

                this.sourceStatusChangedSubject.next();
                this.agentSourceIndexService.refresh();

                this.growl(
                    messageSeverityEnum.error,
                    'AutomationAgents.growl.sourceStatusFailed.summary',
                    'AutomationAgents.growl.sourceStatusFailed.detail'
                );

                return of(null);
            })
        );
    }

    /**
     * Asks first - removing a source takes its filters with it and cannot be undone - and only
     * emits once it has actually gone, so a caller dropping the row from its list is not acting on
     * a removal the user backed out of.
     */
    removeSource(agent: CurationAgentModel, source: CurationAgentSourceModel): Observable<null> {
        if (!agent.id || !source.id) {
            return of(null);
        }

        return new Observable<null>(subscriber => {
            this.coreDialogService.open(ConfirmDialogComponent, {
                inputs: {
                    id: 'curationAgents.confirmRemoveSource',
                    heading: 'AutomationAgents.confirmRemoveSource.heading',
                    alertType: messageSeverityEnum.warning,
                    alertMessage: this.localizationService.transform(
                        'AutomationAgents.confirmRemoveSource.alertMessage',
                        { name: agent.name }
                    ),
                    message: 'AutomationAgents.confirmRemoveSource.message',
                    okLabel: 'Core.button.remove'
                },
                outputs: {
                    dialogClose: (confirmed: boolean) => {
                        if (!confirmed) {
                            subscriber.complete();

                            return;
                        }

                        this.deleteSource(agent, source).subscribe({
                            next: value => subscriber.next(value),
                            error: error => subscriber.error(error),
                            complete: () => subscriber.complete()
                        });
                    }
                }
            } as ConfirmDialogModel);
        });
    }

    private deleteSource(agent: CurationAgentModel, source: CurationAgentSourceModel): Observable<null> {
        return this.curationAgentsApi.deleteSource(agent.id, source.id).pipe(
            switchMap(() => this.deleteSourceFilters([source])),
            map(() => null as null),
            tap(() => {
                this.refresh();
                this.growlWithSourceName(
                    source,
                    messageSeverityEnum.success,
                    'AutomationAgents.growl.sourceRemoved.summary',
                    'AutomationAgents.growl.sourceRemoved.detail',
                    agent.name
                );
            }),
            // Completes without emitting on failure, so the caller leaves the row where it is
            catchError(() => {
                this.growl(
                    messageSeverityEnum.error,
                    'AutomationAgents.growl.sourceRemoveFailed.summary',
                    'AutomationAgents.growl.sourceRemoveFailed.detail',
                    agent.name
                );

                return EMPTY;
            })
        );
    }

    /**
     * Fills in what each action points at by name - the API stores only the id, so a reloaded agent
     * would otherwise read "Folder: 1757" on every card that lists its actions.
     */
    private loadActionNames(agent: CurationAgentModel): Observable<CurationAgentModel> {
        const actions = [...agent.positiveActions, ...agent.negativeActions]
            .filter(action => action.referenceIds.length);

        if (!actions.length) {
            return of(agent);
        }

        return combineLatest([
            this.folderService.all$.pipe(take(1), catchError(() => of([]))),
            this.tagsService.all$.pipe(take(1), catchError(() => of([]))),
            this.rssFeedService.all$.pipe(take(1), catchError(() => of([]))),
            this.coverageAnalysisService.customInsights$.pipe(take(1), catchError(() => of([])))
        ]).pipe(
            take(1),
            map(([folders, tags, feeds, insights]) => {
                const names = new Map<string, string>();

                const add = (types: CurationActionTypeEnum[], items: { id: number; name: string }[]) =>
                    types.forEach(type => (items ?? []).forEach(
                        item => names.set(actionNameKey(type, item.id), item.name)
                    ));

                add(folderActionTypes, folders);
                add(tagActionTypes, tags);
                add([CurationActionTypeEnum.sendToRss], feeds);
                add([CurationActionTypeEnum.generateInsight], insights);

                // An action can target several folders or tags, so every id is named in place
                actions.forEach(action => action.referenceNames = action.referenceIds.map(
                    (referenceId, index) => names.get(actionNameKey(action.type, referenceId))
                        ?? action.referenceNames[index]
                        ?? null
                ));

                return agent;
            })
        );
    }

    /**
     * Opens what an action targets - the folder or tag it saves to - in a new tab. An action can
     * hold several, so the caller says which one was clicked; without one, the first is opened.
     */
    viewActionTarget(action: CurationAgentActionModel, referenceId: string = action.referenceId): void {
        const type = folderActionTypes.includes(action.type)
            ? MonitoringSeriesTypeEnum.folder
            : tagActionTypes.includes(action.type) ? MonitoringSeriesTypeEnum.tag : null;

        if (!type || !referenceId) {
            return;
        }

        const url = this.coverageUrl(type, Number(referenceId));

        if (url) {
            window.open(url);
        }
    }

    /**
     * Opens the coverage behind a source in a new tab, the way the monitoring source picker's View
     * Source link does - straight to the topic or folder, or to saved coverage filtered to it.
     */
    viewSource(source: CurationAgentSourceModel): void {
        const url = this.sourceUrl(source);

        if (!url) {
            this.growl(
                messageSeverityEnum.error,
                'AutomationAgents.growl.viewSourceFailed.summary',
                'AutomationAgents.growl.viewSourceFailed.detail'
            );

            return;
        }

        window.open(url);
    }

    /**
     * Opens the coverage an ad hoc run was submitted from, the same way {@link viewSource} opens a
     * live source's. A run only knows where it came from once the service records it, so callers
     * check {@link hasAdHocSource} before offering this.
     */
    viewAdHocSource(run: AgentAdhocSourceResponse): void {
        const type = run?.sourceType as MonitoringSeriesTypeEnum;
        const id = Number(run?.sourceRef);

        if (!this.coverageUrl(type, id)) {
            this.growl(
                messageSeverityEnum.error,
                'AutomationAgents.growl.viewSourceFailed.summary',
                'AutomationAgents.growl.viewSourceFailed.detail'
            );

            return;
        }

        // The run's filters live in the saved search it was submitted with, so they are read back
        // and travel with the link the same way a live source's do. Opened first and pointed at the
        // coverage once that has come back, or the browser treats it as a popup and blocks it.
        const tab = window.open('', '_blank');

        this.adHocFilters(run).subscribe(source => {
            const url = this.coverageUrl(type, id, source);

            if (tab) {
                tab.location.href = url;

                return;
            }

            window.open(url);
        });
    }

    /** The run's narrowing, as a source the coverage link can carry. Null where it had none */
    private adHocFilters(run: AgentAdhocSourceResponse): Observable<CurationAgentSourceModel> {
        if (!run?.filterSearchCriteriaId) {
            return of(null);
        }

        const source = new CurationAgentSourceModel();

        source.dataSource = new MonitoringBackEndDataSourceModel(
            run.sourceType as MonitoringSeriesTypeEnum,
            Number(run.sourceRef)
        );

        return this.savedSearches.getById(run.filterSearchCriteriaId).pipe(
            map(saved => {
                source.filterLoaded(new MentionSearchCriteriaModel(saved as unknown as SearchCriteriaModel));

                return source;
            }),
            // A filter that has gone missing must not stop the coverage from opening
            catchError(() => of(null))
        );
    }

    /** Whether a run carries the coverage it was submitted from */
    hasAdHocSource(run: AgentAdhocSourceResponse): boolean {
        return !!run?.sourceType && !!run?.sourceRef;
    }

    /** Where a source lives, or null for the types that have nowhere of their own to land on */
    private sourceUrl(source: CurationAgentSourceModel): string {
        return this.coverageUrl(source?.dataSource?.type, source?.dataSource?.id, source);
    }

    /**
     * Where coverage of a given type lives. `source` is only needed when its filters should travel
     * with the link; an action targets the folder or tag itself, unfiltered.
     */
    private coverageUrl(type: MonitoringSeriesTypeEnum, id: number, source?: CurationAgentSourceModel): string {
        if (!id || !type) {
            return null;
        }

        // Types with a page of their own go straight there; the rest land on saved coverage with
        // the source applied as a filter, which is how the picker reaches them
        const pages: Partial<Record<MonitoringSeriesTypeEnum, string>> = {
            [MonitoringSeriesTypeEnum.topic]: `Monitoring/topics/topic/${id}`,
            [MonitoringSeriesTypeEnum.subscription]: `Monitoring/subscriptions/subscription/${id}`,
            [MonitoringSeriesTypeEnum.folder]: `Monitoring/mentions/folder/${id}`,
            [MonitoringSeriesTypeEnum.theme]: `Social/themes/theme/${id}/postFeed`,
            [MonitoringSeriesTypeEnum.aiAgent]: agentSectionRoute(id, AgentRouteSectionEnum.mentions).substring(1)
        };

        const page = pages[type];

        // Whatever the picker was narrowed by travels with the link, the same way its own View
        // Source control carries it - a page of its own still opens filtered
        if (page) {
            const filters = source ? this.sourceFilterQuery(source) : null;

            return this.appBaseHref + page + (filters ? `?filters=${filters}` : '');
        }

        const filtered: Partial<Record<MonitoringSeriesTypeEnum, keyof MentionSearchQueryModel>> = {
            [MonitoringSeriesTypeEnum.tag]: 'tag',
            [MonitoringSeriesTypeEnum.tagGroup]: 'tagGroup',
            [MonitoringSeriesTypeEnum.sourceGroup]: 'sourceGroup'
        };

        const field = filtered[type];

        if (!field) {
            return null;
        }

        // These have no page of their own, so the source itself becomes a filter alongside the rest
        const criteria = source?.dataSource?.filterSearchCriteria?.clone() ?? new MentionSearchCriteriaModel();
        (criteria.query[field] as BasicNumberFilterModel).include = [id];

        return `${this.appBaseHref}Monitoring/mentions/saved`
            + `?filters=${MentionSearchCriteriaModel.toFilterQueryString(criteria, uuid.v4())}`;
    }

    /** The row's filters as a query string, or null when it has none to carry */
    private sourceFilterQuery(source: CurationAgentSourceModel): string {
        return source.hasFilters
            ? MentionSearchCriteriaModel.toFilterQueryString(source.dataSource.filterSearchCriteria, uuid.v4())
            : null;
    }

    delete(agent: CurationAgentModel): Observable<null> {
        // Read before the agent goes: its sources are what hold the filter ids, and they are
        // deleted along with it
        return this.curationAgentsApi.listSources(agent.id).pipe(
            catchError(() => of([])),
            switchMap(sources => this.curationAgentsApi.delete(agent.id).pipe(
                switchMap(() => this.deleteSourceFilters(sources)),
                map(() => null as null)
            )),
            tap(() => {
                this.refresh();
                this.growl(
                    messageSeverityEnum.success,
                    'AutomationAgents.growl.deleted.summary',
                    'AutomationAgents.growl.deleted.detail',
                    agent.name
                );
            }),
            catchError(() => {
                this.growl(
                    messageSeverityEnum.error,
                    'AutomationAgents.growl.deleteFailed.summary',
                    'AutomationAgents.growl.deleteFailed.detail',
                    agent.name
                );

                return of(null);
            })
        );
    }

    /**
     * Sources have no bulk endpoint, so bring the saved set in line with what the panel holds by
     * removing the ones that are gone and adding the ones that are new. Untouched rows are left be
     * so their status and scan history survive an edit.
     */
    private saveSources(agentId: number, agent: CurationAgentModel): Observable<unknown> {
        return this.saveSourceFilters(agentId, agent).pipe(
            switchMap(() => this.reconcileSources(agentId, agent))
        );
    }

    /** Adds what is new and removes what is gone, leaving untouched rows their status and history */
    private reconcileSources(agentId: number, agent: CurationAgentModel): Observable<unknown> {
        return this.curationAgentsApi.listSources(agentId).pipe(
            switchMap(existing => {
                const wanted = agent.completeSources;

                const removed = existing.filter(source => !wanted.some(row => row.matches(source)));
                const added = wanted.filter(row => !existing.some(source => row.matches(source)));

                if (!removed.length && !added.length) {
                    return of([]);
                }

                // Removals first, and only then the additions: a row whose filters changed is a
                // remove plus an add of the same source, which the (agent, type, ref) unique key
                // rejects as a duplicate if the two are in flight together.
                const removals = removed.length
                    ? forkJoin(removed.map(source => this.curationAgentsApi.deleteSource(agentId, source.id))).pipe(
                        switchMap(() => this.deleteSourceFilters(removed))
                    )
                    : of([]);

                return removals.pipe(
                    switchMap(() => added.length
                        ? forkJoin(added.map(row => this.curationAgentsApi.createSource(agentId, row.toCreateRequest())))
                        : of([]))
                );
            })
        );
    }


    /** The growl's title for whichever status the source ended up in */
    private sourceStatusSummary(status: CurationAgentSourceStatusEnum): string {
        if (status === CurationAgentSourceStatusEnum.standby) {
            return 'AutomationAgents.growl.sourceStatus.summary.standby';
        }

        return status === CurationAgentSourceStatusEnum.active
            ? 'AutomationAgents.growl.sourceStatus.summary.active'
            : 'AutomationAgents.growl.sourceStatus.summary.paused';
    }

    private sourceStatusDetail(status: CurationAgentSourceStatusEnum): string {
        if (status === CurationAgentSourceStatusEnum.standby) {
            return 'AutomationAgents.growl.sourceStatus.standby';
        }

        return status === CurationAgentSourceStatusEnum.active
            ? 'AutomationAgents.growl.sourceStatus.active'
            : 'AutomationAgents.growl.sourceStatus.paused';
    }

    /** The agent-side wording of a status growl, keyed by the status the service settled on */
    private agentStatusKey(part: 'summary' | 'detail', status: CurationAgentSourceStatusEnum): string {
        const settled = status === CurationAgentSourceStatusEnum.active || status === CurationAgentSourceStatusEnum.standby
            ? status
            : CurationAgentSourceStatusEnum.paused;

        return `AutomationAgents.growl.agentStatus.${part}.${settled.toLowerCase()}`;
    }

    /**
     * A growl whose message names the source it is about. The name is only known through
     * {@link sourceNames$}, so the message is built once that has resolved.
     */
    private growlWithSourceName(
        source: CurationAgentSourceModel,
        severity: messageSeverityEnum,
        summary: string,
        detail: string,
        agentName?: string,
        sourceType?: string
    ): void {
        const fallback = String(source?.dataSource?.id ?? '');

        this.sourceNames$.pipe(
            take(1),
            map(names => names.get(sourceNameKey(source?.dataSource?.type, source?.dataSource?.id)) ?? fallback),
            catchError(() => of(fallback))
        ).subscribe(sourceName => this.messageService.add({
            severity: severity,
            summary: this.localizationService.transform(summary),
            detail: this.localizationService.transform(detail, { name: sourceName, agent: agentName, type: sourceType })
        }));
    }

    private growl(severity: messageSeverityEnum, summary: string, detail: string, name?: string): void {
        this.messageService.add({
            severity: severity,
            summary: this.localizationService.transform(summary),
            detail: this.localizationService.transform(detail, { name: name })
        });
    }
}
